import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import { NULL_ADDRESS } from 'shared/constants/constants';
import {
  MovesEnum,
  RoomStatusEnum,
  RoundWinnerOutcomes,
  WinnerStatusEnum,
} from 'shared/constants/shared-enums';
import { usePublicClient } from 'shared/hooks/use-web3';
import { initialState, useNewRoomStore } from 'shared/store/room-store';
import { Phase, useStreamStore } from 'shared/store/stream-store';

interface IUseStreamRoomProps {
  isGuestMode: boolean;
}

export const useStreamRoom = ({ isGuestMode }: IUseStreamRoomProps) => {
  const { t } = useTranslation();
  const { networkId, activeRoomId } = useParams();
  const location = useLocation();

  const useSnapshot = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('replay') === '1';
  }, [location.search]);

  const [playerACardsAnimating, setPlayerACardsAnimating] = useState(false);
  const [playerBCardsAnimating, setPlayerBCardsAnimating] = useState(false);
  const [playerAWonRound, setPlayerAWonRound] = useState(false);
  const [playerBWonRound, setPlayerBWonRound] = useState(false);
  const [roundDraw, setRoundDraw] = useState(false);
  const [playerASelectedMove, setPlayerASelectedMove] = useState(MovesEnum.None);
  const [playerBSelectedMove, setPlayerBSelectedMove] = useState(MovesEnum.None);
  const [playerAWins, setPlayerAWins] = useState(0);
  const [playerBWins, setPlayerBWins] = useState(0);
  const [roomDescription, setRoomDescription] = useState('');

  const { room, rounds, initialLoading, stopPolling, startStopRoom } = useNewRoomStore();
  const { currentPhase, currentRound, queue, manageQueue } = useStreamStore();

  const chainId = Number(networkId);
  const publicClient = usePublicClient();

  const isGameFinished = queue?.length === 0 && room.roomStatus === RoomStatusEnum.Closed;

  // Drive the animation phases for live AND replay
  useEffect(() => {
    if (initialLoading) return;

    // For live: isGuestMode may be true (disconnected), still animate
    // For replay: isGuestMode is also fine — we animate from preloaded snapshot
    let phase: Phase = 'break';
    if (
      !rounds?.[room?.roundCounter - 1]?.playerA?.encrMove &&
      !rounds?.[room?.roundCounter - 1]?.playerB?.encrMove
    ) {
      phase = 'playersThinking';
    } else if (
      (rounds?.[room?.roundCounter - 1]?.playerA?.encrMove &&
        !rounds?.[room?.roundCounter - 1]?.playerB?.encrMove) ||
      (rounds?.[room?.roundCounter - 1]?.playerB?.encrMove &&
        !rounds?.[room?.roundCounter - 1]?.playerA?.encrMove)
    ) {
      phase = 'onePlayerChooseMove';
    } else if (
      rounds?.[room?.roundCounter - 1]?.playerA?.move &&
      rounds?.[room?.roundCounter - 1]?.playerB?.move
    ) {
      phase = 'done';
    }
    manageQueue({ round: room.roundCounter, phase });
  }, [room, rounds, networkId, isGuestMode, initialLoading, manageQueue]);

  // Start/stop polling depending on replay/live
  useEffect(() => {
    // Clear previous polling/state on route param change
    stopPolling();
    useNewRoomStore.setState((s) => ({ ...s, ...initialState, initialLoading: true }));

    if (useSnapshot) {
      // Replay mode: only read from snapshot; no polling
      const data = (window as any).__STREAM_META__;
      setRoomDescription(data?.roomDescription || '');
      return () => {
        stopPolling();
      };
    }

    // Live mode: poll chain by URL chainId (works when disconnected)
    if (activeRoomId && publicClient && Number.isFinite(chainId)) {
      startStopRoom({ provider: publicClient, roomId: +activeRoomId });
    } else {
      startStopRoom({ provider: null, roomId: null });
    }

    return () => {
      stopPolling();
    };
  }, [activeRoomId, networkId, chainId, publicClient, useSnapshot, startStopRoom, stopPolling]);

  // UI state driven by queue changes
  useEffect(() => {
    if (!currentPhase) return;

    if (currentPhase === 'onePlayerChooseMove' || currentPhase === 'twoPlayersChooseMove') {
      setPlayerACardsAnimating(true);
      setPlayerBCardsAnimating(true);
    }

    if (currentPhase === 'playersMadeReveal') {
      const round = rounds?.[currentRound - 1];
      setPlayerACardsAnimating(false);
      setPlayerBCardsAnimating(false);
      setPlayerAWonRound(round?.winner === RoundWinnerOutcomes.PlayerA);
      setPlayerBWonRound(round?.winner === RoundWinnerOutcomes.PlayerB);
      setRoundDraw(round?.winner === RoundWinnerOutcomes.Draw);
      setPlayerASelectedMove(round?.playerA?.move || MovesEnum.None);
      setPlayerBSelectedMove(round?.playerB?.move || MovesEnum.None);

      let a = 0;
      let b = 0;
      const roundsToCount = rounds?.slice(0, currentRound);
      roundsToCount?.forEach((r) => {
        if (r.winner === RoundWinnerOutcomes.PlayerA) a++;
        if (r.winner === RoundWinnerOutcomes.PlayerB) b++;
      });
      setPlayerAWins(a);
      setPlayerBWins(b);
    }

    if (currentPhase === 'done' && queue.length !== 0 && queue.length !== 1) {
      setPlayerAWonRound(false);
      setPlayerBWonRound(false);
      setPlayerASelectedMove(MovesEnum.None);
      setPlayerBSelectedMove(MovesEnum.None);
    }

    if (!queue.length) {
      setPlayerACardsAnimating(false);
      setPlayerBCardsAnimating(false);
    }
  }, [currentPhase, currentRound, rounds, queue]);

  const renderEndRoundPhrase = () => {
    if (isGameFinished) {
      return (
        <div className="absolute left-1/2 top-[150px] min-w-[420px] -translate-x-1/2 transform text-center sm:top-[294px] lg:top-[250px]">
          <div className="gradient-title-gold text-uppercase text-[28px] font-[900] leading-[32px] sm:text-[64px] sm:leading-[80px] lg:text-[54px]">
            {(room.winnerStatus === WinnerStatusEnum.WinnerPlayerA ||
              room.winnerStatus === WinnerStatusEnum.TechnicalWinnerA) &&
              t('modals.watchGame.playerWon', { number: '1' })}
            {(room.winnerStatus === WinnerStatusEnum.WinnerPlayerB ||
              room.winnerStatus === WinnerStatusEnum.TechnicalWinnerB) &&
              t('modals.watchGame.playerWon', { number: '2' })}
          </div>
          <div className="text-[14px] font-[900] leading-[20px] text-[#fff] sm:text-[32px] sm:leading-[40px] lg:text-[28px] lg:leading-[64px]">
            {room.winnerStatus === WinnerStatusEnum.TechnicalWinnerA
              ? t('modals.watchGame.playerSurrendered', { number: '2' })
              : room.winnerStatus === WinnerStatusEnum.TechnicalWinnerB
                ? t('modals.watchGame.playerSurrendered', { number: '1' })
                : ''}
          </div>
        </div>
      );
    }

    if (
      rounds?.[currentRound - 1]?.winner === RoundWinnerOutcomes.PlayerA &&
      currentPhase === 'done'
    ) {
      return (
        <div className="absolute left-1/2 top-[150px] z-40 flex min-w-[420px] -translate-x-1/2 transform flex-col items-center text-center sm:top-[294px] lg:top-[250px]">
          <div className="gradient-title-gold text-uppercase text-[28px] font-[900] leading-[32px] sm:text-[64px] sm:leading-[80px] lg:text-[54px]">
            {t('modals.watchGame.roundWinnerTitle', { number: '1' })}
          </div>
          <div className="text-[14px] font-[900] leading-[20px] text-[#fff] sm:text-[32px] sm:leading-[40px] lg:text-[28px] lg:leading-[64px]">
            {t('modals.watchGame.roundWinnerSubtitle')}
          </div>
        </div>
      );
    }

    if (
      rounds?.[currentRound - 1]?.winner === RoundWinnerOutcomes.PlayerB &&
      currentPhase === 'done'
    ) {
      return (
        <div className="absolute left-1/2 top-[150px] z-40 flex min-w-[420px] -translate-x-1/2 transform flex-col items-center text-center sm:top-[294px] lg:top-[250px]">
          <div className="g:text-[54px] gradient-title-gold text-uppercase text-[28px] font-[900] leading-[32px] sm:text-[64px] sm:leading-[80px]">
            {t('modals.watchGame.roundWinnerTitle', { number: '2' })}
          </div>
          <div className="text-[14px] font-[900] leading-[20px] text-white sm:text-[32px] sm:leading-[40px] lg:text-[28px] lg:leading-[64px]">
            {t('modals.watchGame.roundWinnerSubtitle')}
          </div>
        </div>
      );
    }

    if (
      rounds?.[currentRound - 1]?.winner === RoundWinnerOutcomes.Draw &&
      currentPhase === 'done'
    ) {
      return (
        <div className="absolute left-1/2 top-[150px] z-40 flex min-w-[420px] -translate-x-1/2 transform flex-col items-center text-center sm:top-[320px] lg:top-[270px]">
          <div className="gradient-title text-uppercase text-[28px] font-[900] leading-[32px] sm:text-[64px] sm:leading-[80px] lg:text-[60px]">
            {t('modals.watchGame.draw')}
          </div>
        </div>
      );
    }
    return null;
  };

  return {
    isInitialLoading: initialLoading,
    is404:
      !initialLoading &&
      room.playerA.address === NULL_ADDRESS &&
      room.playerB.address === NULL_ADDRESS,
    playerACardsAnimating,
    playerBCardsAnimating,
    playerAWonRound,
    playerBWonRound,
    roundDraw,
    playerASelectedMove,
    playerBSelectedMove,
    playerAWins,
    playerBWins,
    currentPhase,
    currentRound,
    queue,
    room,
    roomDescription,
    renderEndRoundPhrase,
  };
};
